// src/services/users.service.ts

import {
  collection, getDocs, doc, updateDoc,
  query, where, orderBy, limit, startAfter, getCountFromServer,
  type QueryDocumentSnapshot, type DocumentData,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { UserRole } from '@/config/routes.config';

export interface SystemUser {
  uid: string;
  fullName: string;
  email: string;
  nomina: string;
  roleSGC: UserRole;
  accesoSGC: boolean;
}

export interface UsersPage {
  users: SystemUser[];
  total: number;
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
}

const toUser = (d: QueryDocumentSnapshot<DocumentData>): SystemUser => {
  const data = d.data();
  return {
    uid: d.id,
    fullName: data.fullName ?? '',
    email: data.email ?? '',
    nomina: data.nomina ?? '',
    roleSGC: (data.roleSGC as UserRole) ?? 'Lector',
    accesoSGC: data.accesoSGC === true,
  };
};

class UsersService {
  private col = 'users';

  /** Carga una página de todos los usuarios ordenados por fullName. */
  async getPage(
    pageSize: number,
    after: QueryDocumentSnapshot<DocumentData> | null = null,
  ): Promise<UsersPage> {
    const col = collection(db, this.col);

    // Conteo total de usuarios
    const { count: total } = (await getCountFromServer(query(col))).data();

    // Query paginada
    const paged: any[] = [orderBy('fullName'), limit(pageSize)];
    if (after) paged.push(startAfter(after));

    const snap = await getDocs(query(col, ...paged));
    return {
      users: snap.docs.map(toUser),
      total,
      lastDoc: snap.docs[snap.docs.length - 1] ?? null,
    };
  }

  async updateUser(uid: string, updates: Partial<Pick<SystemUser, 'accesoSGC' | 'roleSGC'>>): Promise<void> {
    await updateDoc(doc(db, this.col, uid), updates);
  }

  /** Devuelve usuarios filtrados por acceso, ordenados por nombre.
   *  Para accesoSGC=true:  consulta Firestore directamente.
   *  Para accesoSGC=false: trae todos y filtra en cliente,
   *    porque los documentos sin el campo accesoSGC también cuentan como "sin acceso".
   */
  async getFilteredByAccess(accesoSGC: boolean): Promise<SystemUser[]> {
    if (accesoSGC) {
      // Solo los que tienen accesoSGC === true
      const snap = await getDocs(
        query(
          collection(db, this.col),
          where('accesoSGC', '==', true),
        )
      );
      const users = snap.docs.map(toUser);
      return users.sort((a, b) => a.fullName.localeCompare(b.fullName, 'es'));
    } else {
      // Sin acceso: incluye accesoSGC === false Y documentos sin el campo
      const snap = await getDocs(collection(db, this.col));
      const users = snap.docs.map(toUser).filter((u) => u.accesoSGC !== true);
      return users.sort((a, b) => a.fullName.localeCompare(b.fullName, 'es'));
    }
  }

  /** Busca usuarios por nombre o nómina (máx 50 resultados combinados). */
  async searchByName(q: string): Promise<SystemUser[]> {
    const normalized = q.charAt(0).toUpperCase() + q.slice(1);
    const col = collection(db, this.col);

    // Búsqueda por nombre
    const nameSnap = await getDocs(
      query(
        col,
        orderBy('fullName'),
        where('fullName', '>=', normalized),
        where('fullName', '<=', normalized + '\uf8ff'),
        limit(25),
      )
    );

    // Búsqueda por nómina (exacta o que empiece con)
    const nominaSnap = await getDocs(
      query(
        col,
        where('nomina', '>=', q),
        where('nomina', '<=', q + '\uf8ff'),
        limit(25),
      )
    );

    // Combinar resultados y eliminar duplicados
    const userMap = new Map<string, SystemUser>();

    nameSnap.docs.forEach((doc) => {
      const user = toUser(doc);
      userMap.set(user.uid, user);
    });

    nominaSnap.docs.forEach((doc) => {
      const user = toUser(doc);
      userMap.set(user.uid, user);
    });

    // Convertir a array y ordenar por nombre
    return Array.from(userMap.values())
      .sort((a, b) => a.fullName.localeCompare(b.fullName, 'es'))
      .slice(0, 50);
  }

  async getReviewers(): Promise<SystemUser[]> {
    const snap = await getDocs(
      query(
        collection(db, this.col),
        where('accesoSGC', '==', true),
        where('roleSGC', 'in', ['Revisor', 'Aprobador', 'Responsable_Calidad']),
      )
    );
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        uid: d.id,
        fullName: data.fullName ?? '',
        email: data.email ?? '',
        nomina: data.nomina ?? '',
        roleSGC: data.roleSGC as UserRole,
        accesoSGC: true,
      };
    });
  }

  /** Devuelve solo usuarios con rol Aprobador con acceso activo. */
  async getAprobadores(): Promise<SystemUser[]> {
    const snap = await getDocs(
      query(
        collection(db, this.col),
        where('accesoSGC', '==', true),
        where('roleSGC', '==', 'Aprobador'),
      )
    );
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        uid: d.id,
        fullName: data.fullName ?? '',
        email: data.email ?? '',
        nomina: data.nomina ?? '',
        roleSGC: data.roleSGC as UserRole,
        accesoSGC: true,
      };
    });
  }

  /**
   * Busca el departamento de un usuario en UsuariosDcc a partir de su nómina.
   * En users: nomina es string. En UsuariosDcc: no es int.
   */
  async getDepartamentoByNomina(nomina: string): Promise<string | null> {
    const parsed = parseInt(nomina, 10);
    if (isNaN(parsed)) return null;
    const snap = await getDocs(
      query(
        collection(db, 'UsuariosDcc'),
        where('no', '==', parsed),
        limit(1),
      ),
    );
    if (snap.empty) return null;
    return (snap.docs[0].data().departamento as string) ?? null;
  }

  /** Busca la foto de usuario en UsuariosDcc a partir de la nómina. */
  async getFotoByNomina(nomina: string): Promise<string | null> {
    const raw = nomina?.trim();
    if (!raw) return null;

    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed)) {
      const byNumber = await getDocs(
        query(
          collection(db, 'UsuariosDcc'),
          where('no', '==', parsed),
          limit(1),
        ),
      );
      if (!byNumber.empty) return (byNumber.docs[0].data().foto as string) ?? null;
    }

    const byString = await getDocs(
      query(
        collection(db, 'UsuariosDcc'),
        where('no', '==', raw),
        limit(1),
      ),
    );
    if (byString.empty) return null;
    return (byString.docs[0].data().foto as string) ?? null;
  }

  /** Lee la colección departamentos (lista canónica). */
  async getDepartamentos(): Promise<{ id: string; nombre: string }[]> {
    const snap = await getDocs(
      query(collection(db, 'departamentos'), orderBy('nombre', 'asc')),
    );
    return snap.docs.map((d) => ({ id: d.id, nombre: d.data().nombre as string }));
  }

  /** Devuelve solo usuarios con rol Responsable_Calidad con acceso activo. */
  async getResponsables(): Promise<SystemUser[]> {
    const snap = await getDocs(
      query(
        collection(db, this.col),
        where('accesoSGC', '==', true),
        where('roleSGC', '==', 'Responsable_Calidad'),
      )
    );
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        uid: d.id,
        fullName: data.fullName ?? '',
        email: data.email ?? '',
        nomina: data.nomina ?? '',
        roleSGC: data.roleSGC as UserRole,
        accesoSGC: true,
      };
    });
  }

  /** Devuelve solo usuarios con rol Auditor_Interno con acceso activo. */
  async getAuditoresInternos(): Promise<SystemUser[]> {
    const snap = await getDocs(
      query(
        collection(db, this.col),
        where('accesoSGC', '==', true),
        where('roleSGC', '==', 'Auditor_Interno'),
      )
    );
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        uid: d.id,
        fullName: data.fullName ?? '',
        email: data.email ?? '',
        nomina: data.nomina ?? '',
        roleSGC: data.roleSGC as UserRole,
        accesoSGC: true,
      };
    });
  }
}

export const usersService = new UsersService();
